/**
 * Line diff via longest common subsequence.
 *
 * The DP table is O(n·m) in memory, which is fine for the paste-two-files case this
 * tool is for; `MAX_LINES` keeps a pathological paste from allocating gigabytes.
 */

export type Op = "same" | "add" | "remove";

export interface DiffLine {
  op: Op;
  text: string;
  /** 1-based line number in the original text, or null for added lines. */
  leftNo: number | null;
  /** 1-based line number in the changed text, or null for removed lines. */
  rightNo: number | null;
}

export const MAX_LINES = 2000;

export interface DiffResult {
  lines: DiffLine[];
  added: number;
  removed: number;
  /** True when either side was truncated to MAX_LINES. */
  truncated: boolean;
}

export function diffLines(leftText: string, rightText: string): DiffResult {
  const allLeft = leftText.split("\n");
  const allRight = rightText.split("\n");
  const truncated = allLeft.length > MAX_LINES || allRight.length > MAX_LINES;
  const left = allLeft.slice(0, MAX_LINES);
  const right = allRight.slice(0, MAX_LINES);

  const n = left.length;
  const m = right.length;

  // lcs[i][j] = length of the longest common subsequence of left[i:] and right[j:].
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] =
        left[i] === right[j]
          ? (lcs[i + 1]![j + 1] ?? 0) + 1
          : Math.max(lcs[i + 1]![j] ?? 0, lcs[i]![j + 1] ?? 0);
    }
  }

  const lines: DiffLine[] = [];
  let added = 0;
  let removed = 0;
  let i = 0;
  let j = 0;

  while (i < n && j < m) {
    if (left[i] === right[j]) {
      lines.push({ op: "same", text: left[i] ?? "", leftNo: i + 1, rightNo: j + 1 });
      i++;
      j++;
    } else if ((lcs[i + 1]![j] ?? 0) >= (lcs[i]![j + 1] ?? 0)) {
      lines.push({ op: "remove", text: left[i] ?? "", leftNo: i + 1, rightNo: null });
      removed++;
      i++;
    } else {
      lines.push({ op: "add", text: right[j] ?? "", leftNo: null, rightNo: j + 1 });
      added++;
      j++;
    }
  }
  while (i < n) {
    lines.push({ op: "remove", text: left[i] ?? "", leftNo: i + 1, rightNo: null });
    removed++;
    i++;
  }
  while (j < m) {
    lines.push({ op: "add", text: right[j] ?? "", leftNo: null, rightNo: j + 1 });
    added++;
    j++;
  }

  return { lines, added, removed, truncated };
}

/**
 * Drop long runs of unchanged lines, keeping `context` on each side of every change —
 * the same idea as `diff -U`. Returns null when nothing was collapsed.
 */
export function collapse(lines: DiffLine[], context = 3): Array<DiffLine | "gap"> | null {
  const keep = new Set<number>();
  lines.forEach((line, idx) => {
    if (line.op === "same") return;
    for (let k = idx - context; k <= idx + context; k++) {
      if (k >= 0 && k < lines.length) keep.add(k);
    }
  });
  if (keep.size === lines.length) return null;

  const out: Array<DiffLine | "gap"> = [];
  let inGap = false;
  lines.forEach((line, idx) => {
    if (keep.has(idx)) {
      out.push(line);
      inGap = false;
    } else if (!inGap) {
      out.push("gap");
      inGap = true;
    }
  });
  return out;
}
